import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gift as GiftType, GiftActivateResponse } from '@lunit/shared-types';
import { Loader2, Gift, Check, Clock, Coins, Coffee, Tag, Package, X, Send, AlertCircle } from 'lucide-react';
import { getMyGifts, activateGift, confirmGift, getGiftTemplates, type GiftTemplatePublic } from '@core/api/gifts';
import { Card } from '@core/components/Card';
import { Button } from '@core/components/Button';
import { Header } from '@core/components/Header';
import { useProfile } from '@core/contexts/ProfileContext';
import { useIdentity } from '@core/contexts/IdentityContext';

// Format remaining time with more detail
function formatExpiresIn(expiresAt: string): string {
  const diffMs = new Date(expiresAt).getTime() - Date.now();
  if (diffMs <= 0) return 'Истёк';

  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  if (days > 0) return `${days} дн. ${hours} ч.`;
  if (hours > 0) return `${hours} ч. ${minutes} мин.`;
  return `${minutes} мин.`;
}

// Format time remaining as MM:SS (for 10-min code timer)
function formatCodeTimer(expiresAt: string): string {
  const diffMs = new Date(expiresAt).getTime() - Date.now();
  if (diffMs <= 0) return '00:00';
  const minutes = Math.floor(diffMs / 60000);
  const seconds = Math.floor((diffMs % 60000) / 1000);
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

// Live countdown badge with auto-update every minute
function CountdownBadge({ expiresAt }: { expiresAt: string }) {
  const [text, setText] = useState(() => formatExpiresIn(expiresAt));

  useEffect(() => {
    const interval = setInterval(() => setText(formatExpiresIn(expiresAt)), 60_000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  const isUrgent = new Date(expiresAt).getTime() - Date.now() < 24 * 60 * 60 * 1000;

  return (
    <div className={`flex items-center gap-1 text-xs ${isUrgent ? 'text-red-500 font-semibold' : 'text-text-muted'}`}>
      <Clock className="w-3 h-3" />
      {text}
    </div>
  );
}

// Icon by gift type
function GiftTypeIcon({ type }: { type: string }) {
  switch (type) {
    case 'BONUS_POINTS':
      return <Coins className="w-5 h-5" />;
    case 'PRODUCT':
      return <Coffee className="w-5 h-5" />;
    case 'SUBSCRIPTION':
      return <Package className="w-5 h-5" />;
    case 'PROMO_CODE':
      return <Tag className="w-5 h-5" />;
    default:
      return <Gift className="w-5 h-5" />;
  }
}

// Template value label
function getTemplateValueLabel(tpl: GiftTemplatePublic): string {
  const v = tpl.value;
  switch (tpl.type) {
    case 'BONUS_POINTS':
      return `${v.points ?? 0} баллов`;
    case 'PRODUCT':
      return String(v.rewardName ?? 'Продукт');
    case 'SUBSCRIPTION':
      return String(v.planName ?? 'Абонемент');
    case 'PROMO_CODE':
      return `Скидка ${v.discountPercent ?? 0}%`;
    default:
      return '';
  }
}

export function GiftsPage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<GiftTemplatePublic[]>([]);
  const [pending, setPending] = useState<GiftType[]>([]);
  const [activated, setActivated] = useState<GiftType[]>([]);
  const [loading, setLoading] = useState(true);
  const [activatingId, setActivatingId] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [activationResult, setActivationResult] = useState<GiftActivateResponse | null>(null);

  // PRODUCT gift two-step activation state
  const [activeGiftCode, setActiveGiftCode] = useState<{
    code: string; expiresAt: string; giftId: string; giftTitle: string;
  } | null>(null);
  const [codeTimeRemaining, setCodeTimeRemaining] = useState('');
  const [isCodeExpired, setIsCodeExpired] = useState(false);
  const [isCodeUrgent, setIsCodeUrgent] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [confirmSuccess, setConfirmSuccess] = useState(false);

  // Клиент — из сессии входа (K-002): подарки грузятся, как только есть профиль, в обоих входах.
  // Telegram ID — лишь необязательная сверка: он есть только у входа Telegram (в PWA — null, не отправляем).
  const { user: profileUser, loading: profileLoading } = useProfile();
  const { telegramId } = useIdentity();
  const userId = profileUser?.id ?? null;

  // Check for pending gift claim from deep link
  useEffect(() => {
    const claimData = localStorage.getItem('pending-gift-claim');
    if (claimData) {
      try {
        const result = JSON.parse(claimData);
        setMessage({ type: 'success', text: `Подарок «${result.gift?.title || ''}» получен!` });
      } catch {
        // ignore
      }
      localStorage.removeItem('pending-gift-claim');
    }
  }, []);

  // Load gifts + templates + triggered promos
  useEffect(() => {
    if (!userId) {
      if (!profileLoading) setLoading(false);
      return;
    }

    const load = async () => {
      try {
        const [giftsData, tplData] = await Promise.all([
          getMyGifts(telegramId).catch(() => ({ pending: [], activated: [] })),
          getGiftTemplates().catch(() => []),
        ]);
        setPending(giftsData.pending);
        setActivated(giftsData.activated);
        setTemplates(Array.isArray(tplData) ? tplData : []);
      } catch (error) {
        console.error('Failed to load gifts:', error);
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [userId, telegramId, profileLoading]);

  // Timer for PRODUCT code (1-second interval)
  useEffect(() => {
    if (!activeGiftCode) return;

    const updateTimer = () => {
      const diffMs = new Date(activeGiftCode.expiresAt).getTime() - Date.now();
      setCodeTimeRemaining(formatCodeTimer(activeGiftCode.expiresAt));
      setIsCodeUrgent(diffMs > 0 && diffMs < 60_000);
      if (diffMs <= 0) setIsCodeExpired(true);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [activeGiftCode]);

  // Handle activation
  const handleActivate = useCallback(async (gift: GiftType) => {
    if (!userId) return;

    try {
      setActivatingId(gift.id);
      setMessage(null);

      const result = await activateGift(gift.id, telegramId);

      // PRODUCT with code + timer → show code modal (gift stays PENDING until cashier confirms)
      if (result.activationCode && result.codeExpiresAt) {
        setActiveGiftCode({
          code: result.activationCode,
          expiresAt: result.codeExpiresAt,
          giftId: gift.id,
          giftTitle: gift.title,
        });
        setIsCodeExpired(false);
        setConfirmSuccess(false);
        return;
      }

      // Move gift from pending to activated
      setPending(prev => prev.filter(g => g.id !== gift.id));
      setActivated(prev => [result.gift, ...prev]);

      // Show result based on type
      if (result.pointsAdded) {
        setMessage({ type: 'success', text: `+${result.pointsAdded} баллов начислено!` });
      } else if (result.promoCode) {
        setActivationResult(result);
      } else {
        setMessage({ type: 'success', text: 'Подарок активирован!' });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Не удалось активировать подарок';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setActivatingId(null);
    }
  }, [userId, telegramId]);

  // Handle PRODUCT gift confirm ("Получено")
  const handleConfirmGift = useCallback(async () => {
    if (!activeGiftCode) return;

    try {
      setIsConfirming(true);
      await confirmGift(activeGiftCode.code);
      setConfirmSuccess(true);

      // Move gift from pending to activated after 2 seconds
      setTimeout(() => {
        setPending(prev => prev.filter(g => g.id !== activeGiftCode.giftId));
        setActiveGiftCode(null);
        setConfirmSuccess(false);
        setMessage({ type: 'success', text: `Подарок «${activeGiftCode.giftTitle}» получен!` });
      }, 2000);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Ошибка подтверждения';
      setMessage({ type: 'error', text: errorMessage });
      setActiveGiftCode(null);
    } finally {
      setIsConfirming(false);
    }
  }, [activeGiftCode]);

  // Handle cancel PRODUCT code
  const handleCancelGiftCode = useCallback(() => {
    setActiveGiftCode(null);
  }, []);

  // Handle expired code close
  const handleExpiredGiftClose = useCallback(() => {
    setActiveGiftCode(null);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-text-main">
        <Loader2 className="animate-spin" size={32} />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6">
      <Header title="Подарки" showBack />

      {/* Status message */}
      {message && (
        <Card
          className={`px-4 py-3 ${
            message.type === 'success' ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-600'
          }`}
        >
          {message.text}
        </Card>
      )}

      {/* ── МОИ ПОДАРКИ ── */}
      <div className="space-y-3">
        <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wider">
          Мои подарки
        </h2>

        {/* Pending (not yet activated) */}
        {pending.map(gift => (
          <Card key={gift.id} className="border-primary/30">
            <div className="flex gap-3">
              <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <GiftTypeIcon type={gift.type} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-text-main">{gift.title}</div>
                {gift.description && (
                  <div className="text-sm text-text-muted mt-0.5 line-clamp-2">{gift.description}</div>
                )}
                {gift.senderUser && (
                  <div className="text-xs text-text-muted mt-1">
                    От: {[gift.senderUser.firstName, gift.senderUser.lastName].filter(Boolean).join(' ')}
                  </div>
                )}
                <div className="flex items-center justify-between mt-2">
                  <CountdownBadge expiresAt={gift.expiresAt} />
                  <Button
                    size="sm"
                    onClick={() => handleActivate(gift)}
                    disabled={activatingId !== null}
                  >
                    {activatingId === gift.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Забрать подарок'
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        ))}

        {/* Activated (history) */}
        {activated.map(gift => (
          <Card key={gift.id} className="opacity-60">
            <div className="flex gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center flex-shrink-0">
                <Check className="w-5 h-5 text-green-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-text-main text-sm">{gift.title}</div>
                {gift.activatedAt && (
                  <div className="text-xs text-text-muted mt-0.5">
                    Получен {new Date(gift.activatedAt).toLocaleDateString('ru-RU')}
                  </div>
                )}
              </div>
            </div>
          </Card>
        ))}

        {/* Empty state — no gifts at all */}
        {pending.length === 0 && activated.length === 0 && (
          <Card className="text-center py-6">
            <Gift className="w-10 h-10 mx-auto mb-2 text-text-muted opacity-40" />
            <div className="text-text-muted text-sm">Пока нет подарков</div>
          </Card>
        )}
      </div>

      {/* ── ПОДАРИТЬ ДРУГУ ── */}
      {templates.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wider">
            Подарить другу
          </h2>
          {templates.map(tpl => (
            <Card
              key={tpl.id}
              className="cursor-pointer hover:border-primary/40 transition-colors"
              onClick={() => navigate('/send-gift')}
            >
              <div className="flex gap-3">
                <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <GiftTypeIcon type={tpl.type} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-text-main">{tpl.title}</div>
                  {tpl.description && (
                    <div className="text-sm text-text-muted mt-0.5 line-clamp-2">{tpl.description}</div>
                  )}
                  <div className="flex items-center gap-3 mt-2 text-xs text-text-muted">
                    <span className="font-medium text-text-main">{getTemplateValueLabel(tpl)}</span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {tpl.expiresInDays} дн.
                    </span>
                  </div>
                </div>
                <div className="flex items-center flex-shrink-0">
                  <Send className="w-4 h-4 text-text-muted" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Promo code modal (for PROMO_CODE gifts) */}
      {activationResult && activationResult.promoCode && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-sm relative">
            <button
              onClick={() => setActivationResult(null)}
              className="absolute top-3 right-3 text-text-muted hover:text-text-main"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="text-center pt-2 pb-4">
              <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-500" />
              </div>
              <div className="font-semibold text-text-main text-lg mb-2">Ваш промокод</div>
              <div className="text-4xl font-mono font-bold text-primary tracking-widest my-4">
                {activationResult.promoCode}
              </div>
              <div className="text-sm text-text-muted">
                Используйте этот код при оформлении заказа
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* PRODUCT gift code modal — 10-min timer + cashier confirm */}
      {activeGiftCode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-md bg-black/70">
          <div className="relative w-full max-w-sm overflow-hidden rounded-3xl bg-[#0f1729] border border-[#2a3441] shadow-2xl shadow-black/50 animate-in zoom-in-95">
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-gradient-to-br from-amber-500/30 to-orange-500/20 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-gradient-to-tr from-purple-500/20 to-blue-500/20 rounded-full blur-3xl pointer-events-none" />

            {/* Close button */}
            <button
              onClick={handleCancelGiftCode}
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 transition-all duration-200"
              disabled={isConfirming}
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>

            <div className="relative p-6 text-center space-y-5">
              {/* Success State */}
              {confirmSuccess ? (
                <div className="py-8">
                  <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center animate-in zoom-in shadow-xl shadow-emerald-500/40">
                    <Check className="w-12 h-12 text-white" strokeWidth={3} />
                  </div>
                  <div className="text-2xl font-bold text-emerald-400">Получено!</div>
                  <div className="text-gray-400 mt-2">{activeGiftCode.giftTitle}</div>
                </div>
              ) : isCodeExpired ? (
                /* Expired State */
                <div className="py-6">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-red-500/10 border-2 border-red-500/30 flex items-center justify-center">
                    <AlertCircle className="w-10 h-10 text-red-400" />
                  </div>
                  <div className="text-xl font-bold text-red-400 mb-2">Код истёк</div>
                  <div className="text-sm text-gray-500 mb-6">
                    Нажмите «Забрать подарок» снова, чтобы получить новый код
                  </div>
                  <button
                    onClick={handleExpiredGiftClose}
                    className="w-full py-3 px-6 bg-[#1a2332] hover:bg-[#252f3f] border border-[#2a3441] rounded-xl text-white font-medium transition-colors"
                  >
                    Закрыть
                  </button>
                </div>
              ) : (
                /* Active Code State */
                <>
                  <div className="pt-2">
                    <div className="w-18 h-18 mx-auto mb-4 p-4 rounded-2xl bg-gradient-to-br from-amber-400 via-amber-500 to-orange-500 flex items-center justify-center shadow-xl shadow-amber-500/30">
                      <Coffee className="w-10 h-10 text-white" strokeWidth={2} />
                    </div>
                    <h2 className="text-2xl font-bold text-white">Покажите кассиру</h2>
                  </div>

                  <div className="text-lg font-semibold text-white">
                    {activeGiftCode.giftTitle}
                  </div>

                  {/* 6-digit code */}
                  <div className="text-5xl font-mono font-bold text-amber-400 tracking-[0.3em] my-2">
                    {activeGiftCode.code}
                  </div>

                  {/* Timer */}
                  <div className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full border ${
                    isCodeUrgent
                      ? 'bg-red-500/10 border-red-500/30 animate-pulse'
                      : 'bg-amber-500/10 border-amber-500/20'
                  }`}>
                    <Clock className={`w-5 h-5 ${isCodeUrgent ? 'text-red-400' : 'text-amber-400'}`} />
                    <span className={`font-mono text-xl font-bold ${isCodeUrgent ? 'text-red-400' : 'text-amber-400'}`}>
                      {codeTimeRemaining}
                    </span>
                  </div>

                  {/* Confirm Button */}
                  <button
                    onClick={handleConfirmGift}
                    disabled={isConfirming}
                    className="w-full py-4 px-6 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 rounded-xl text-white font-semibold text-lg flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/30 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isConfirming ? (
                      <Loader2 className="w-6 h-6 animate-spin" />
                    ) : (
                      <>
                        <Check className="w-6 h-6" strokeWidth={2.5} />
                        Получено
                      </>
                    )}
                  </button>

                  <button
                    onClick={handleCancelGiftCode}
                    className="w-full py-3 text-sm text-gray-500 hover:text-gray-300 transition-colors border border-[#2a3441] rounded-xl hover:bg-[#1a2332]"
                    disabled={isConfirming}
                  >
                    Отменить
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
